import type { ReactElement, ReactNode } from "react";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Spinner } from "@freightsimple/ui-core/Feedback/Spinner";
import { LoadFailed } from "@/Shared/Components/LoadFailed";

/**
 * The full-width customer page frame: a title + microcopy header (with optional
 * right-aligned actions and a hairline under it), then the page content — plus
 * a centered loading spinner, empty-state, and failed-load state.
 *
 * A renovated, ui-core-internals replacement for the old `Cargo/Layout`
 * version, kept at the same API so screens only change their import. It lives in
 * the dashboard app rather than ui-core on purpose: ui-core already owns the
 * page abstraction (`Page`), and a second full-width layout shouldn't compete
 * with it in the shared library. The eventual convergence is onto ui-core
 * `Page`; until then this keeps these screens off Cargo without changing how
 * they look.
 *
 * Break glass, and the reason is the same one: this IS the page frame, so its
 * chrome has nothing left to compose from. The header row wraps, bottom-aligns
 * and draws its own hairline (`SplitRow` does none of the three), and the spinner
 * and empty state are absolutely positioned overlays pinned below the header
 * (`Center` only centres within normal flow). It stays here rather than being
 * rebuilt around ui-core `Page`, whose icon/stats/control-bar header is a
 * different design for a different audience — 15 customer screens render through
 * this, so that swap is its own piece of work, not a lint cleanup.
 */
interface FullWidthLayoutProps {
  header: ReactNode;
  microcopy?: ReactNode | undefined;
  content: ReactElement | undefined;
  rightContent?: ReactNode | undefined;
  loading?: boolean | undefined;
  showEmptyState?: boolean | undefined;
  emptyState?: ReactElement | undefined;
  /**
   * Set when the page's data failed to load: the header stays and LoadFailed
   * replaces both the content and the empty state, so a failed fetch never
   * reads as "you have none". Called by its Try again button.
   */
  onRetryFailedLoad?: (() => void) | undefined;
}

// The spinner and the empty state both sit pinned and centered across the full
// width, the way the original did — content that has no place in the normal
// header/body flow. Padded and width-capped so the copy centers and wraps on
// narrow screens instead of running edge to edge.
function CenteredOverlay_({ children }: { children: ReactNode }) {
  return (
    // w-full, not w-screen: 100vw includes the vertical scrollbar, so this
    // overlay was 15px wider than the space actually available and gave every
    // loading/empty page a small horizontal scroll.
    <div className="absolute left-0 mt-[180px] flex w-full items-center justify-center px-6">
      {/* 768px, not the 576px this used to cap at: the longest empty-state
          title needs 626px to stay on one line and its microcopy 704px, so the
          tighter cap broke both across two lines — the pre-renovation screens
          set them on one. The long address-book microcopy still wraps here,
          which is what a measure cap is for.

          text-balance stays for the titles that do wrap, so the last word isn't
          stranded on a line of its own. Scoped to headings — balancing a full
          paragraph is not the same win. */}
      <div className="m-auto max-w-3xl text-center [&_h1]:text-balance">
        {children}
      </div>
    </div>
  );
}

function FullWidthLayout(props: FullWidthLayoutProps) {
  if (props.loading) {
    return (
      <CenteredOverlay_>
        <Spinner />
      </CenteredOverlay_>
    );
  }

  const showEmptyState =
    props.showEmptyState === true && props.onRetryFailedLoad === undefined;

  return (
    <div className="h-full w-full pt-12">
      {/* The header wraps rather than pinning one 64px line: rightContent can
          be a tall block (a claim summary, a row of stats), which has nowhere
          to go beside the title on a narrow screen. min-h-16 keeps the roomy
          look on a wide one. */}
      {!showEmptyState && (
        <div className="flex min-h-16 w-full flex-wrap items-end justify-between gap-y-4 border-b border-border pb-2">
          <div>
            <Heading level={1}>{props.header}</Heading>
            <div className="text-base text-content-secondary">
              {props.microcopy}
            </div>
          </div>
          <div className="flex items-center self-stretch">
            {props.rightContent}
          </div>
        </div>
      )}
      <div className="mt-4 min-h-[300px] w-full">
        {props.onRetryFailedLoad !== undefined ? (
          <LoadFailed onRetry={props.onRetryFailedLoad} />
        ) : showEmptyState ? (
          <CenteredOverlay_>{props.emptyState}</CenteredOverlay_>
        ) : (
          (props.content ?? <></>)
        )}
      </div>
    </div>
  );
}
export default FullWidthLayout;
